import * as React from 'react';
import {
  Avatar as ShadcnAvatar,
  AvatarImage as ShadcnAvatarImage,
  AvatarFallback as ShadcnAvatarFallback,
  AvatarBadge as ShadcnAvatarBadge,
  AvatarGroup as ShadcnAvatarGroup,
  AvatarGroupCount as ShadcnAvatarGroupCount,
} from '@/hen-ui/shadcn/avatar';
export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnAvatar> {
  src?: string;
  alt: string;
  fallback?: string;
  size?: 'sm' | 'md' | 'lg';
  badge?: boolean;
}

export interface AvatarGroupProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnAvatarGroup> {
  children: React.ReactNode;
  max?: number;
}

/**
 * Avatar component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui avatar (Radix UI Avatar primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
  <Avatar
    alt="John Doe"
    fallback="JD"
    src="https://randomuser.me/api/portraits/men/32.jpg"
  />
 *
 * @example
 * // Fallback only
  <Avatar
    alt="Jane Doe"
    fallback="JD"
  />
 *
 * @example
 * // Using Avatar Group
    <AvatarGroup>
      <Avatar src='https://randomuser.me/api/portraits/men/11.jpg' alt='User 1' fallback='U1' />
      <Avatar src='https://randomuser.me/api/portraits/women/12.jpg' alt='User 2' fallback='U2' />
      <Avatar src='https://randomuser.me/api/portraits/men/13.jpg' alt='User 3' fallback='U3' />
      <Avatar src='https://randomuser.me/api/portraits/women/14.jpg' alt='User 4' fallback='U4' />
      <Avatar src='https://randomuser.me/api/portraits/men/15.jpg' alt='User 5' fallback='U5' />
    </AvatarGroup>,
 *
 * @example
 * // Using Avatar Group with Overflow
    <AvatarGroup max={2}>
      <Avatar src='https://randomuser.me/api/portraits/men/11.jpg' alt='User 1' fallback='U1' />
      <Avatar src='https://randomuser.me/api/portraits/women/12.jpg' alt='User 2' fallback='U2' />
      <Avatar src='https://randomuser.me/api/portraits/men/13.jpg' alt='User 3' fallback='U3' />
      <Avatar src='https://randomuser.me/api/portraits/women/14.jpg' alt='User 4' fallback='U4' />
      <Avatar src='https://randomuser.me/api/portraits/men/15.jpg' alt='User 5' fallback='U5' />
    </AvatarGroup>,
 */

export const Avatar = React.forwardRef<
  React.ElementRef<typeof ShadcnAvatar>,
  AvatarProps
>(({ className, src, alt, fallback, size, badge, ...props }, ref) => {
  return (
    <ShadcnAvatar ref={ref} className={className} {...props}>
      <ShadcnAvatarImage src={src} alt={alt} />
      <ShadcnAvatarFallback>{fallback}</ShadcnAvatarFallback>
      {badge ? <ShadcnAvatarBadge /> : null}
    </ShadcnAvatar>
  );
});

Avatar.displayName = 'Avatar';

export const AvatarGroup = React.forwardRef<
  React.ElementRef<typeof ShadcnAvatarGroup>,
  AvatarGroupProps
>(({ className, children, max, ...props }, ref) => {
  let childrenArray = React.Children.toArray(children);
  let overflowCount = 0;

  if (max) {
    const showCount = max && childrenArray.length > max;
    if (showCount) {
      overflowCount = childrenArray.length - max;
      childrenArray = childrenArray.slice(0, max);
    }
  }

  return (
    <ShadcnAvatarGroup ref={ref} className={className} {...props}>
      {childrenArray}
      {overflowCount > 0 && (
        <ShadcnAvatarGroupCount>+{overflowCount}</ShadcnAvatarGroupCount>
      )}
    </ShadcnAvatarGroup>
  );
});

AvatarGroup.displayName = 'AvatarGroup';
